import { isEmpty } from './utils';

export { fill, create2d };

/**
 * Fills array with missing items
 * @template T
 * @param source Source array.
 * @param count The required number of items in array.
 * @param clone The callback function to clone existing item.
 * @param ctor The callback function to create new missing item.
 * @param [resultMap] The callback function to map each item in array.
 * @returns  Array with amount of items equals to `count`.
 */
const fill = <T = any>(
    source: T[],
    count: number,
    clone: (item: T) => T,
    ctor: (index: number) => T,
    resultMap: (item: T, index: number) => T = null) => {

    const cloneFunc = clone || ((p) => p);
    let result = (source || []).map(item => cloneFunc(item))

    result = [
        ...result,
        ...Array(count - result.length).fill({}).map((x, index) => ctor(index)),
    ]

    return resultMap
        ? result.map((item, index) => resultMap(item, index))
        : result
}

type ValueInitializerCallback = (r: number, c: number) => any

const create2d = (rows: number, cols: number, defaultValue: any | ValueInitializerCallback) => {
    const arr = [];
    for (let r = 0; r < rows; r++) {
        arr.push(Array(cols));

        for (let c = 0; c < cols; c++) {
            arr[r][c] = typeof defaultValue === 'function'
                ? defaultValue(r, c)
                : defaultValue;
        }
    }

    return arr;
}

declare global {
    interface Array<T> {
        indexOf2d<T>(this: T[][], el);
        lastIndexOf2d<T>(this: T[][], el);
        pad2d<T>(this: T[][], pad: number, defaultValue: any);
        first<T>(this: T[]): T;
        skip<T>(this: T[], predicate: (value: T) => boolean): T[];
    }
}

Array.prototype.indexOf2d = function <T>(this: T[][], el) {
    for (let row = 0; row < this.length; row++) {
        const col = this[row].indexOf(el);

        if (col > -1) {
            return {
                x: col,
                y: row,
            }
        }
    }
};

Array.prototype.lastIndexOf2d = function <T>(this: T[][], el) {
    for (let row = this.length - 1; row >= 0; row--) {
        const col = this[row].lastIndexOf(el);
        if (col > -1) {
            return {
                x: col,
                y: row,
            }
        }
    }
};

Array.prototype.pad2d = function <T>(this: T[][], pad: number, defaultValue: any) {
    const rows = this.length + (pad * 2)
    const columns = this[0].length + (pad * 2)

    const result = create2d(rows, columns, defaultValue)

    for (let r = pad; r < rows - pad; r++) {
        result[r] = [
            ...result[r].slice(0, pad),
            ...this[r - pad],
            ...result[r].slice(-pad),

        ]
    }

    return result
};

Array.prototype.first = function <T>(this: T[]): T {
    return !isEmpty(this) ? this[0] : null;
};

Array.prototype.skip = function <T>(this: T[], predicate: (value) => boolean): T[] {
    return this.filter(p => !predicate(p));
};
